import React, { useEffect, useRef, useState } from "react";
import { GEO_STYLES, WALL_KINDS, wallKindOf, wallThicknessOf, floorRegions, resolveZoneName, polyArea } from "@/lib/geo";
import type { Vertex, Wall, Opening, GeometryStatus, ZoneDef } from "@/api/types";

export interface EditableGeom {
  vertices: Vertex[];
  walls: Wall[];
  openings: Opening[];
  zones?: ZoneDef[];
  lowCeilingArea?: number;
  lowCeilingPolys?: { x: number; y: number }[][];
  height: number;
  status: GeometryStatus;
}

export type EditorTool =
  | "move-vertex" | "move-wall" | "draw-wall"
  | "add-door" | "add-window" | "delete" | "correct-opening" | "draw-lowceiling";

interface Props {
  geom: EditableGeom;
  editable?: boolean;
  tool?: EditorTool;
  selectedOpening?: string | null;
  selectedWall?: string | null;
  drawFromId?: string | null;        // id of last drawn vertex (for rubber-band preview)
  drawStartId?: string | null;       // id of first vertex of current path (for auto-close)
  snap?: number | null;              // grid step in meters (null/0 = off)
  angleSnap?: boolean;               // snap draw direction to 45° increments
  showDims?: boolean;                // render wall lengths
  showArea?: boolean;                // render floor area when the room is closed
  fitKey?: string | number;          // changing this refits the (frozen) view to content
  onSelectOpening?: (id: string | null) => void;
  onChange?: (g: EditableGeom) => void;
  onBeginChange?: () => void;        // called once at drag start (history snapshot)
  onEmptyClick?: (p: { x: number; y: number }) => void;
  onWallClick?: (wallId: string, position: number) => void;
  onVertexClick?: (vertexId: string) => void;
  onOpeningClick?: (openingId: string) => void;
  onWallDoubleClick?: (wallId: string) => void;   // double-click to edit wall nature/thickness
  onZoneDoubleClick?: (cx: number, cy: number) => void;  // double-click a zone label to rename
  onClosePath?: () => void;          // called when the current path should auto-close
  lowCeilDraft?: { x: number; y: number }[];        // in-progress sous-pente polygon
  onLowCeilPoint?: (p: { x: number; y: number }) => void;
  onLowCeilClose?: () => void;
  className?: string;
}

const vById = (vs: Vertex[], id: string) => vs.find((v) => v.id === id);

export const GeometryCanvas2D: React.FC<Props> = ({
  geom, editable = false, tool = "move-vertex", selectedOpening, selectedWall, drawFromId, drawStartId,
  snap = null, angleSnap = false, showDims = false, showArea = false, fitKey,
  onSelectOpening, onChange, onBeginChange, onEmptyClick, onWallClick, onVertexClick,
  onOpeningClick, onWallDoubleClick, onZoneDoubleClick, onClosePath,
  lowCeilDraft, onLowCeilPoint, onLowCeilClose, className = "",
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ type: "vertex" | "wall"; orig: Record<string, { x: number; y: number }>; ox: number; oy: number; moved: boolean } | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const style = GEO_STYLES[geom.status];

  const xs = geom.vertices.map((v) => v.x);
  const ys = geom.vertices.map((v) => v.y);
  const minX = Math.min(...xs, 0), maxX = Math.max(...xs, 5);
  const minY = Math.min(...ys, 0), maxY = Math.max(...ys, 5);
  const pad = Math.max((maxX - minX), (maxY - minY)) * 0.2 + 0.8;
  const auto = { x: minX - pad, y: minY - pad, w: maxX - minX + pad * 2, h: maxY - minY + pad * 2 };

  // frozen view: in editable mode the view only refits when fitKey changes (no jumps while drawing)
  const frozen = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  useEffect(() => { frozen.current = auto; /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fitKey]);
  const view = editable ? (frozen.current ?? auto) : auto;

  const diag = Math.hypot(view.w, view.h);
  const vr = diag * 0.016;
  const dimSize = diag * 0.032;
  const closeDist = vr * 2.6;

  const gcx = xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : (minX + maxX) / 2;
  const gcy = ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : (minY + maxY) / 2;

  const toWorld = (clientX: number, clientY: number) => {
    const svg = svgRef.current!;
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const p = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    return { x: p.x, y: p.y };
  };

  const drawFrom = drawFromId ? vById(geom.vertices, drawFromId) : null;

  // snap a drawing point: existing vertex first, else angle (45°) then grid
  const snapPoint = (p: { x: number; y: number }) => {
    const vSnap = snap && snap > 0 ? snap * 2 : 0.2;
    for (const vv of geom.vertices) {
      if (vv.id === drawFromId) continue;
      if (Math.hypot(vv.x - p.x, vv.y - p.y) <= vSnap) return { x: vv.x, y: vv.y };
    }
    let x = p.x, y = p.y;
    if (angleSnap && drawFrom) {
      const dx = x - drawFrom.x, dy = y - drawFrom.y;
      const len = Math.hypot(dx, dy);
      if (len > 1e-4) {
        const step = Math.PI / 4;
        const ang = Math.round(Math.atan2(dy, dx) / step) * step;
        x = drawFrom.x + Math.cos(ang) * len;
        y = drawFrom.y + Math.sin(ang) * len;
      }
    }
    if (snap && snap > 0) { x = Math.round(x / snap) * snap; y = Math.round(y / snap) * snap; }
    return { x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000 };
  };

  const snapN = (n: number) => (snap && snap > 0 ? Math.round(n / snap) * snap : Math.round(n * 1000) / 1000);

  const wallPosition = (wall: Wall, p: { x: number; y: number }) => {
    const a = vById(geom.vertices, wall.from), b = vById(geom.vertices, wall.to);
    if (!a || !b) return 0.5;
    const dx = b.x - a.x, dy = b.y - a.y;
    const len2 = dx * dx + dy * dy || 1;
    const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    return Math.max(0.05, Math.min(0.95, t));
  };

  // auto-close affordance
  const drawStart = drawStartId ? vById(geom.vertices, drawStartId) : null;
  const pathWallCount = drawStart
    ? geom.walls.filter((w) => w.from === drawStartId || w.to === drawStartId).length
    : 0;
  const canClose = !!(drawStart && drawFromId && drawFromId !== drawStartId && pathWallCount >= 1 &&
    geom.walls.length >= 2);
  const nearStart = !!(canClose && hover && Math.hypot(hover.x - drawStart!.x, hover.y - drawStart!.y) < closeDist);

  // ---- drag (move tools) ----
  const onVertexDown = (id: string) => (e: React.PointerEvent) => {
    if (!editable || tool !== "move-vertex") return;
    e.stopPropagation();
    const v = vById(geom.vertices, id);
    if (!v) return;
    const w = toWorld(e.clientX, e.clientY);
    onBeginChange?.();
    drag.current = { type: "vertex", orig: { [id]: { x: v.x, y: v.y } }, ox: w.x, oy: w.y, moved: false };
    (e.target as Element).setPointerCapture(e.pointerId);
  };
  const onWallDown = (id: string) => (e: React.PointerEvent) => {
    if (!editable || tool !== "move-wall") return;
    e.stopPropagation();
    const wall = geom.walls.find((wl) => wl.id === id);
    const a = wall && vById(geom.vertices, wall.from);
    const b = wall && vById(geom.vertices, wall.to);
    if (!wall || !a || !b) return;
    const w = toWorld(e.clientX, e.clientY);
    onBeginChange?.();
    drag.current = { type: "wall", orig: { [a.id]: { x: a.x, y: a.y }, [b.id]: { x: b.x, y: b.y } }, ox: w.x, oy: w.y, moved: false };
    (e.target as Element).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (editable && ((tool === "draw-wall" && drawFromId) || tool === "draw-lowceiling")) {
      setHover(toWorld(e.clientX, e.clientY));
    }
    if (!editable || !drag.current || !onChange) return;
    const w = toWorld(e.clientX, e.clientY);
    const dx = w.x - drag.current.ox;
    const dy = w.y - drag.current.oy;
    if (Math.abs(dx) > 0.001 || Math.abs(dy) > 0.001) drag.current.moved = true;
    const orig = drag.current.orig;
    const vertices = geom.vertices.map((v) =>
      orig[v.id] ? { ...v, x: snapN(orig[v.id].x + dx), y: snapN(orig[v.id].y + dy) } : v
    );
    onChange({ ...geom, vertices });
  };
  const onPointerUp = () => { drag.current = null; };

  // ---- clicks (draw / add / delete / select) ----
  const bgClick = (e: React.MouseEvent) => {
    if (!editable) return;
    const raw = toWorld(e.clientX, e.clientY);
    if (tool === "draw-lowceiling" && onLowCeilPoint) {
      if (lowCeilDraft && lowCeilDraft.length >= 3) {
        const f = lowCeilDraft[0];
        if (Math.hypot(raw.x - f.x, raw.y - f.y) < closeDist) { onLowCeilClose?.(); return; }
      }
      onLowCeilPoint({ x: snapN(raw.x), y: snapN(raw.y) });
      return;
    }
    if (tool !== "draw-wall" || !onEmptyClick) return;
    if (canClose && drawStart && Math.hypot(raw.x - drawStart.x, raw.y - drawStart.y) < closeDist) {
      onClosePath?.();
      return;
    }
    onEmptyClick(snapPoint(raw));
  };
  const wallClick = (wall: Wall) => (e: React.MouseEvent) => {
    if (!editable) return;
    if (!["add-door", "add-window", "delete"].includes(tool)) return;
    e.stopPropagation();
    onWallClick?.(wall.id, wallPosition(wall, toWorld(e.clientX, e.clientY)));
  };
  const vertexClick = (id: string) => (e: React.MouseEvent) => {
    if (!editable) return;
    if (tool === "draw-wall") {
      e.stopPropagation();
      if (canClose && id === drawStartId) { onClosePath?.(); return; }
      const v = vById(geom.vertices, id);
      if (v) onEmptyClick?.({ x: v.x, y: v.y });
      return;
    }
    if (tool !== "delete") return;
    e.stopPropagation();
    onVertexClick?.(id);
  };
  const openingClick = (id: string) => (e: React.MouseEvent) => {
    if (!editable) return;
    if (!["correct-opening", "delete"].includes(tool)) return;
    e.stopPropagation();
    onOpeningClick?.(id);
    onSelectOpening?.(id);
  };

  const openingGeom = (op: Opening) => {
    const wall = geom.walls.find((w) => w.id === op.wall_id);
    if (!wall) return null;
    const a = vById(geom.vertices, wall.from), b = vById(geom.vertices, wall.to);
    if (!a || !b) return null;
    const px = a.x + (b.x - a.x) * op.position;
    const py = a.y + (b.y - a.y) * op.position;
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const ux = (b.x - a.x) / len, uy = (b.y - a.y) / len;
    const half = (op.width || 0.9) / 2;
    return { x1: px - ux * half, y1: py - uy * half, x2: px + ux * half, y2: py + uy * half };
  };

  // ---- dimension labels: push outside polygon + de-overlap ----
  const dimLabels: Array<{ id: string; x: number; y: number; text: string }> = [];
  if (showDims) {
    const placed: Array<{ x: number; y: number }> = [];
    const minSep = dimSize * 1.6;
    for (const wall of geom.walls) {
      const a = vById(geom.vertices, wall.from), b = vById(geom.vertices, wall.to);
      if (!a || !b) continue;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 0.05) continue;
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      // outward direction = away from the geometry centroid
      let ox = mx - gcx, oy = my - gcy;
      const on = Math.hypot(ox, oy);
      if (on < 1e-3) { ox = -(b.y - a.y) / len; oy = (b.x - a.x) / len; } else { ox /= on; oy /= on; }
      let k = dimSize * 0.9;
      let px = mx + ox * k, py = my + oy * k;
      let tries = 0;
      while (tries < 6 && placed.some((p) => Math.hypot(p.x - px, p.y - py) < minSep)) {
        k += minSep * 0.7;
        px = mx + ox * k; py = my + oy * k;
        tries++;
      }
      placed.push({ x: px, y: py });
      dimLabels.push({ id: wall.id, x: px, y: py, text: `${len.toFixed(2)} m` });
    }
  }

  const { outer, regions } = floorRegions(geom.vertices, geom.walls);
  const totalArea = regions.reduce((s, r) => s + r.area, 0);
  const multiZone = regions.length > 1;
  const floorPts = outer
    ? outer.poly.map((p) => `${p.x},${p.y}`).join(" ")
    : (geom.vertices.length >= 3 ? geom.vertices.map((v) => `${v.x},${v.y}`).join(" ") : null);

  const cursor = tool === "draw-wall" || tool === "add-door" || tool === "add-window" || tool === "draw-lowceiling"
    ? "crosshair" : tool === "delete" ? "pointer" : "default";

  const previewTo = drawFrom && hover ? snapPoint(hover) : null;
  const liveLen = drawFrom && previewTo ? Math.hypot(previewTo.x - drawFrom.x, previewTo.y - drawFrom.y) : 0;

  return (
    <svg
      ref={svgRef}
      data-testid="canvas-2d-editor"
      viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
      className={`w-full rounded-lg bg-[#0B111E] tech-grid touch-none ${className}`}
      style={{ aspectRatio: `${view.w} / ${view.h}`, cursor }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={() => { onPointerUp(); setHover(null); }}
    >
      {/* clickable background for drawing */}
      <rect x={view.x} y={view.y} width={view.w} height={view.h} fill="transparent" onClick={bgClick} />

      {/* floor fill (outer boundary) */}
      {floorPts && (
        <polygon points={floorPts} fill={style.fill} stroke="none" pointerEvents="none" />
      )}

      {/* sous-pente (low-ceiling) polygons */}
      {(geom.lowCeilingPolys || []).map((poly, i) => {
        if (!poly || poly.length < 3) return null;
        const pts = poly.map((p) => `${p.x},${p.y}`).join(" ");
        const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
        const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
        return (
          <g key={`lc-${i}`} data-testid={`low-ceiling-poly-${i}`} pointerEvents="none">
            <polygon points={pts} fill="#F59E0B" fillOpacity={0.16} stroke="#F59E0B" strokeOpacity={0.85} strokeWidth={1.5} strokeDasharray="4,3" vectorEffect="non-scaling-stroke" />
            <text x={cx} y={cy} fontSize={diag * 0.024} fontFamily="monospace" fill="#FBBF24" stroke="#0B111E" strokeWidth={diag * 0.024 * 0.2} paintOrder="stroke" textAnchor="middle" dominantBaseline="middle">
              {`sous-pente ${polyArea(poly).toFixed(2)} m²`}
            </text>
          </g>
        );
      })}

      {/* wall thickness bands (to scale) */}
      {geom.walls.map((wall) => {
        const a = vById(geom.vertices, wall.from), b = vById(geom.vertices, wall.to);
        if (!a || !b) return null;
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len < 1e-4) return null;
        const kind = wallKindOf(wall.kind);
        const ks = WALL_KINDS[kind];
        const th = wallThicknessOf(wall);
        const nx = -(b.y - a.y) / len, ny = (b.x - a.x) / len;
        const h = th / 2;
        const pts = `${a.x + nx * h},${a.y + ny * h} ${b.x + nx * h},${b.y + ny * h} ${b.x - nx * h},${b.y - ny * h} ${a.x - nx * h},${a.y - ny * h}`;
        const sel = selectedWall === wall.id;
        return (
          <polygon
            key={`band-${wall.id}`}
            data-testid={`wall-band-${wall.id}`}
            points={pts}
            fill={ks.fill} fillOpacity={sel ? Math.min(1, ks.opacity + 0.25) : ks.opacity}
            stroke={sel ? "#F8FAFC" : "none"} strokeWidth={sel ? 1.5 : 0}
            vectorEffect="non-scaling-stroke"
            style={{ cursor: editable ? "pointer" : "inherit" }}
            onDoubleClick={(e) => { if (editable) { e.stopPropagation(); onWallDoubleClick?.(wall.id); } }}
            onClick={wallClick(wall)}
          />
        );
      })}

      {/* walls */}
      {geom.walls.map((wall) => {
        const a = vById(geom.vertices, wall.from), b = vById(geom.vertices, wall.to);
        if (!a || !b) return null;
        return (
          <line
            key={wall.id}
            x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            stroke={style.stroke}
            strokeWidth={3}
            strokeDasharray={style.dash === "none" ? undefined : style.dash}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            style={{ cursor: editable && (tool === "move-wall" || tool === "delete" || tool === "add-door" || tool === "add-window") ? "pointer" : "inherit" }}
            onPointerDown={onWallDown(wall.id)}
            onDoubleClick={(e) => { if (editable) { e.stopPropagation(); onWallDoubleClick?.(wall.id); } }}
            onClick={wallClick(wall)}
          />
        );
      })}

      {/* floor area per sub-space (survives internal partitions) */}
      {showArea && regions.map((r, i) => {
        const zoneName = resolveZoneName(r, geom.zones, i);
        return (
          <g
            key={`zone-${i}`}
            data-testid={`zone-label-${i}`}
            style={{ cursor: editable ? "pointer" : "default" }}
            onDoubleClick={(e) => { if (editable && onZoneDoubleClick) { e.stopPropagation(); onZoneDoubleClick(r.cx, r.cy); } }}
          >
            <text
              data-testid={`zone-name-${i}`}
              x={r.cx} y={r.cy - diag * 0.032}
              fontSize={diag * 0.028} fontFamily="monospace" fontWeight={600}
              fill="#94A3B8" stroke="#0B111E" strokeWidth={diag * 0.028 * 0.2}
              paintOrder="stroke" textAnchor="middle" dominantBaseline="middle"
            >
              {zoneName}
            </text>
            <text
              data-testid={i === 0 ? "floor-area-label" : `zone-area-${i}`}
              x={r.cx} y={r.cy}
              fontSize={diag * (multiZone ? 0.038 : 0.045)} fontFamily="monospace" fontWeight={700}
              fill="#34D399" stroke="#0B111E" strokeWidth={diag * 0.045 * 0.2}
              paintOrder="stroke" textAnchor="middle" dominantBaseline="middle"
            >
              {`${r.area.toFixed(2)} m²`}
            </text>
          </g>
        );
      })}
      {showArea && multiZone && (
        <text
          data-testid="floor-area-total"
          x={view.x + diag * 0.05} y={view.y + diag * 0.05}
          fontSize={diag * 0.03} fontFamily="monospace" fontWeight={700}
          fill="#22D3EE" stroke="#0B111E" strokeWidth={diag * 0.03 * 0.22}
          paintOrder="stroke" textAnchor="start" dominantBaseline="middle"
          pointerEvents="none"
        >
          {`Total ${totalArea.toFixed(2)} m²`}
        </text>
      )}

      {/* wall dimensions */}
      {dimLabels.map((d) => (
        <text
          key={`dim-${d.id}`}
          data-testid={`wall-dim-${d.id}`}
          x={d.x} y={d.y}
          fontSize={dimSize} fontFamily="monospace"
          fill="#CBD5E1" stroke="#0B111E" strokeWidth={dimSize * 0.18}
          paintOrder="stroke" textAnchor="middle" dominantBaseline="middle"
          pointerEvents="none"
        >
          {d.text}
        </text>
      ))}

      {/* rubber-band preview while drawing */}
      {editable && tool === "draw-wall" && drawFrom && previewTo && (
        <>
          <line x1={drawFrom.x} y1={drawFrom.y} x2={previewTo.x} y2={previewTo.y} stroke="#22D3EE" strokeWidth={2} strokeDasharray="4,3" vectorEffect="non-scaling-stroke" pointerEvents="none" />
          {liveLen > 0.05 && (
            <text
              x={(drawFrom.x + previewTo.x) / 2} y={(drawFrom.y + previewTo.y) / 2 - dimSize * 0.6}
              fontSize={dimSize} fontFamily="monospace" fill="#22D3EE"
              stroke="#0B111E" strokeWidth={dimSize * 0.18} paintOrder="stroke"
              textAnchor="middle" dominantBaseline="middle" pointerEvents="none"
            >
              {`${liveLen.toFixed(2)} m`}
            </text>
          )}
        </>
      )}

      {/* auto-close affordance */}
      {editable && tool === "draw-wall" && drawStart && canClose && (
        <circle
          cx={drawStart.x} cy={drawStart.y} r={closeDist}
          fill={nearStart ? "rgba(52,211,153,0.22)" : "none"}
          stroke={nearStart ? "#34D399" : "#22D3EE"}
          strokeWidth={1.5} strokeDasharray="3,2"
          vectorEffect="non-scaling-stroke" pointerEvents="none"
        />
      )}

      {/* sous-pente draft (in-progress) */}
      {editable && tool === "draw-lowceiling" && lowCeilDraft && lowCeilDraft.length > 0 && (
        <g pointerEvents="none">
          {lowCeilDraft.length >= 2 && (
            <polyline points={lowCeilDraft.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#F59E0B" strokeWidth={2} strokeDasharray="4,3" vectorEffect="non-scaling-stroke" />
          )}
          {hover && (
            <line x1={lowCeilDraft[lowCeilDraft.length - 1].x} y1={lowCeilDraft[lowCeilDraft.length - 1].y} x2={snapN(hover.x)} y2={snapN(hover.y)} stroke="#FBBF24" strokeWidth={1.5} strokeDasharray="3,3" vectorEffect="non-scaling-stroke" />
          )}
          {lowCeilDraft.map((p, i) => (
            <circle key={`lcd-${i}`} cx={p.x} cy={p.y} r={vr * 0.8} fill={i === 0 ? "#34D399" : "#FBBF24"} stroke="#0B111E" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
          ))}
          {lowCeilDraft.length >= 3 && (
            <circle cx={lowCeilDraft[0].x} cy={lowCeilDraft[0].y} r={closeDist} fill="none" stroke="#34D399" strokeWidth={1.5} strokeDasharray="3,2" vectorEffect="non-scaling-stroke" />
          )}
        </g>
      )}

      {/* openings */}
      {geom.openings.map((op) => {
        const g = openingGeom(op);
        if (!g) return null;
        const sel = selectedOpening === op.id;
        const color = op.type === "door" ? "#A78BFA" : "#38BDF8";
        return (
          <g key={op.id} onClick={openingClick(op.id)} style={{ cursor: editable && (tool === "correct-opening" || tool === "delete") ? "pointer" : "inherit" }}>
            {/* hit area */}
            <line x1={g.x1} y1={g.y1} x2={g.x2} y2={g.y2} stroke="transparent" strokeWidth={14} vectorEffect="non-scaling-stroke" />
            <line x1={g.x1} y1={g.y1} x2={g.x2} y2={g.y2} stroke={sel ? "#F8FAFC" : color} strokeWidth={sel ? 7 : 5} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </g>
        );
      })}

      {/* vertices */}
      {geom.vertices.map((v) => {
        const isFrom = v.id === drawFromId;
        const isStart = v.id === drawStartId && canClose;
        return (
          <circle
            key={v.id}
            cx={v.x} cy={v.y} r={isStart && nearStart ? vr * 1.3 : vr}
            fill={isStart ? (nearStart ? "#34D399" : "#0B111E") : isFrom ? "#22D3EE" : "#0B111E"}
            stroke={isStart ? "#34D399" : isFrom ? "#22D3EE" : style.vertex}
            strokeWidth={2.5}
            vectorEffect="non-scaling-stroke"
            style={{ cursor: editable && (tool === "move-vertex" || tool === "delete" || (tool === "draw-wall" && isStart)) ? "pointer" : "inherit" }}
            onPointerDown={onVertexDown(v.id)}
            onClick={vertexClick(v.id)}
          />
        );
      })}
    </svg>
  );
};
